import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import ClickOutsideHolder from "../ClickOutsideHolder";
import ErrorBlock from "../ErrorBlock";
import { withTranslation } from "react-i18next";
import ReactSVG from "react-svg";
import Loader from "../Loader";
import SmartPortal from "../SmartPortal";
import useDebounce from "../../../../custom-hooks/useDebounce";

import "./styles.scss";

const ComboBoxMultipleCheckbox = props => {
    const {
        t,
        meta,
        input,
        placeholder,
        disabled,
        label,
        required,
        isLoading,
        showValuesCount,
        options: initOptions = [],
        disallow_select_all,
        withSearch = true, // always true due to SDSUI-8568, BA-configuration can't be changed to include this flag, and in attributes filters form it's another separate config
        submitComboboxMulty,
        submitOnBlur,
        onOpen,
        isAsync,
        onInputChange,
        initialValues,
        customSelectPortalClassName,
        isSelectPortalMenuFlexible,
        withAsyncSearch,
        onMenuClose,
    } = props;

    const [showOptions, setShowOptions] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const comboboxInputRef = useRef(null);
    const searchInputRef = useRef(null);

    const { debouncedValue: debouncedQuery, setDebouncedValue: setDebouncedQuery } = useDebounce(searchQuery, 300);

    const options = useMemo(() => {
        if (isAsync) {
            // merge together initialValues with current values and get unique options (without duplicates)
            const initKeys = initialValues && Array.isArray(initialValues) ? initialValues.map(item => item.value) : [];
            const inputKeys =
                input && input.value && Array.isArray(input.value) ? input.value.map(item => item.value) : [];
            const initOptionsKeys =
                initOptions && Array.isArray(initOptions) ? initOptions.map(option => option.value) : [];
            const uniqueKeys = [...new Set([...initKeys, ...inputKeys, ...initOptionsKeys])];
            const mergedOptions = [...(input.value || []), ...(initOptions || []), ...(initialValues || [])];
            const options = uniqueKeys.map(key => mergedOptions.find(option => option.value === key));
            return options;
            // return initOptions.length > 0 ? initOptions : input.value;
        }
        if (debouncedQuery) {
            const query = debouncedQuery.toLowerCase();
            // get selected options (to keep them once options are being filtered)
            const selectedOptions =
                input && input.value && Array.isArray(input.value)
                    ? input.value.map(val => initOptions.find(option => option.value === val))
                    : [];


            // make sure to not include selected options in filtered options to prevent duplicate options
            const filteredOptions = initOptions.filter(option => {
                const isMatchQuery = option && option.label && option.label.toLowerCase().includes(query);
                // const isSelected =
                //     input && input.value && Array.isArray(input.value) ? input.value.includes(option.value) : false;
                return isMatchQuery;
                // return isMatchQuery && !isSelected;
            });

            // we need to find not available (filtered) selected options
            // upon search/filter - options list change, once we select an option(s)
            // and make another search - a new list of options received, which could not contain selected option(s) from prev list
            // so we basically seach if there is a selected option in the current filtered options list
            const filteredSelectedOptions = selectedOptions.filter(selectedOption => {
                const isInFiltered = filteredOptions.find(option => option.value === selectedOption.value);
                return !isInFiltered;
            });

            // return [...selectedOptions, ...filteredOptions]; // with selectedOptions selected options will alway jump on top of the list upon select
            return [...filteredSelectedOptions, ...filteredOptions];
        }
        return initOptions;
    }, [initOptions, debouncedQuery, isAsync]);

    const disabledOptions = useMemo(() => {
        if (!disabled) {
            return options;
        }
        return options.filter(option => {
            const isChecked =
                input &&
                Array.isArray(input.value) &&
                input.value.some(item => (isAsync ? item.value === option.value : item === option.value));
            return isChecked;
        });
    }, [options]);

    const showSearch = showOptions && ((withSearch && initOptions?.length >= 7) || withAsyncSearch);

    useEffect(() => {
        if (isAsync) {
            onInputChange(debouncedQuery);
        }
    }, [isAsync, debouncedQuery]);

    const wrapperClassName = cx("input-element comboBox-multi-checkbox", {
        isOpen: showOptions,
        disabled,
        "not-empty": input && input.value.length > 0,
    });

    const inputClassName = cx("input-element", {
        "input-element__error": meta && meta.touched && meta.error,
    });
    const buttonClassName = cx("input-field", {
        "input-field__error": meta && meta.touched && meta.error,
        placeholder: placeholder,
    });

    const showErrorBlock = meta && meta.touched && !meta.active && meta.error;

    const getLabel = value => {
        if (isAsync) {
            const option = options.find(option => option.value === value.value);
            return option && option.label ? option.label.trim() : "";
        } else {
            const option = options.find(option => option.value === value);
            return option && option.label ? option.label.trim() : "";
        }
    };

    // prevent space key from triggering click (which will trigger the options to toggle => hide)
    // but we also update search query - so we cann keep the structure where input is inside of the html button
    // we also get the cursor position so update the new value into the proper position and move the curesor afterwards
    const handleButtonKeyDown = e => {
        if (e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            const inputEl = searchInputRef && searchInputRef.current;
            if (inputEl) {
                const { selectionStart, selectionEnd, value } = inputEl;
                const newValue = value.slice(0, selectionStart) + " " + value.slice(selectionEnd);
                setSearchQuery(newValue);
                // move cursor after inserted space
                requestAnimationFrame(() => {
                    inputEl.selectionStart = inputEl.selectionEnd = selectionStart + 1;
                });
            }
        }
    };

    const handleChange = (event, option) => {
        const value = isAsync ? option : option.value;
        const newValue = [...input.value];
        if (event.target.checked) {
            newValue.push(value);
        } else {
            newValue.splice(newValue.indexOf(value), 1);
        }
        input.onChange(newValue);
        // select search if it is available so user can further filter/search
        if (searchInputRef && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 0); // 200 ms delay to wait until popup is visible
        }
    };

    const clearAll = event => {
        // clear
        input.onChange([]);
        // select search if it is available so user can further filter/search
        if (searchInputRef && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 0); // 200 ms delay to wait until popup is visible
        }
    };

    const toggleSelectAll = event => {
        const optionsLength = options.length;
        const valuesLength = input.value.length;
        if (optionsLength === valuesLength) {
            // clear
            input.onChange([]);
        } else {
            // select
            input.onChange(isAsync ? options : options.map(item => item.value));
        }
        // select search if it is available so user can further filter/search
        if (searchInputRef && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 0); 
        }
    };

    const toggleOptions = (e) => {
        // ignore clicks inside the SEARCH input
        if (searchInputRef && e && e.target){
            if (searchInputRef.current && searchInputRef.current.contains(e.target)) {
                return; // do nothing, user clicked inside search input
            }
        }
        if (!disabled) {
            if (showOptions) {
                input.onBlur();
                setSearchQuery("");
                setDebouncedQuery("");
                if (onMenuClose && typeof onMenuClose === "function") {
                    onMenuClose();
                }
            } else {
                input.onFocus();
            }
            if (onOpen && typeof onOpen === "function") {
                onOpen();
            }
            setShowOptions(prev => !prev);
        }
    };

    const hideOptions = e => {
        const fieldKey = input && input.name;
        let isFieldControllerParent = false;
        let isFieldControllerSubmit = false; // is field controller submit button clicked

        if (fieldKey) {
            const path = e ? e.path || (e.composedPath && e.composedPath()) : [];
            for (let i = 0; i < path.length; i++) {
                if (path[i].getAttribute && path[i].getAttribute("data-field-key")) {
                    isFieldControllerParent = path[i].getAttribute("data-field-key") === fieldKey;
                    // break;
                }
                if (path[i].getAttribute && path[i].getAttribute("data-field-controller-submit")) {
                    isFieldControllerSubmit = path[i].getAttribute("data-field-controller-submit") === "true";
                    // break;
                }
            }
        }
        
        // make sure that not submit button was clicked, if submit button was clicked => options should be hidden
        if (isFieldControllerParent && !isFieldControllerSubmit) {
            // we need to put the focus again to the search if present (upon copy/revert/clear clicks)
            if (searchInputRef && searchInputRef.current) {
                setTimeout(() => {
                    searchInputRef.current.focus();
                }, 0);
            }
            return;
        }

        if (showOptions) {
            input.onBlur();
            setSearchQuery("");
            setDebouncedQuery("");
            if (onMenuClose && typeof onMenuClose === "function") {
                onMenuClose();
            }
            setShowOptions(false);
        }
        const { value, name } = input;
        if (submitOnBlur && meta.touched) {
            submitComboboxMulty(value, name);
        }
    };

    const renderCheckboxGroup = () => {
        const valuesLength = input.value.length;

        const optionsToRender = disabled ? disabledOptions : options;
        const optionsLength = optionsToRender.length;

        const isEmptyDisabledOptions = disabled && optionsLength === 0;

        // do not render empty disabled options menu
        if (isEmptyDisabledOptions) {
            return null;
        }

        return (
            <SmartPortal
                portalClassName={cx("portal-combobox-multi-select-menu", {
                    [customSelectPortalClassName]: customSelectPortalClassName,
                    "options-disabled": disabled,
                })}
                targetElement={comboboxInputRef && comboboxInputRef.current}
                portalPlacement="bottom inner-right"
                portalKey={"SmartPortalComboboxMenu"}
                fieldKey={input && input.name}
                // onClickOutside={() => console.log("*** OUTSIDE ***")}
                outerOffset={4}
                repositionTrigger={input && input.value}
                withTargetWidth
                isSelectPortalMenuFlexible={isSelectPortalMenuFlexible}
            >
                <div className="optionsWrapper comboBoxMultipleCheckbox">
                    {/* {!disabled && (
                        <div className="buttonsWrapper">
                            {((withSearch && initOptions?.length >= 7) || withAsyncSearch) && (
                                <div className="input-element">
                                    <input
                                        ref={searchInputRef}
                                        name={`${input.name}-SEARCH`}
                                        placeholder={t("Search")}
                                        className="input-field"
                                        type="text"
                                        onChange={e => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                    )} */}
                    <div className="options">
                        {!disabled && (
                            <>
                                <button
                                    type="button"
                                    className={cx("btn btn-add button-clear-all", {
                                        disabled: valuesLength === 0 || disabled,
                                    })}
                                    onClick={clearAll}
                                >
                                    <ReactSVG className="button-icon" src={`/data/svg/remove_circle.svg`} />
                                    <span>{t("clear all results")}</span>
                                </button>
                                <div
                                    className={cx("checkbox checkbox-select-all", {
                                        disabled: optionsLength === 0 || disabled,
                                    })}
                                >
                                    {!disallow_select_all && (
                                        <label htmlFor="selectAll">
                                            <input
                                                type="checkbox"
                                                name="selectAll"
                                                id="selectAll"
                                                checked={optionsLength !== 0 && optionsLength === valuesLength}
                                                onChange={toggleSelectAll}
                                            />
                                            <span className="checkbox-text">
                                                <ReactSVG
                                                    className="checkbox__unchecked-icon"
                                                    src={`/data/svg/checkbox_unchecked.svg`}
                                                />
                                                <ReactSVG
                                                    className="checkbox__checked-icon"
                                                    src={`/data/svg/checkbox_checked.svg`}
                                                />
                                                <span>{t("select all results")}</span>
                                            </span>
                                        </label>
                                    )}
                                </div>
                            </>
                        )}
                        {optionsToRender.map((option, index) => {
                            const isChecked =
                                input &&
                                Array.isArray(input.value) &&
                                input.value.some(item =>
                                    isAsync ? item.value === option.value : item === option.value
                                );
                            return (
                                <div className={cx("checkbox", { disabled: option.disabled || disabled })} key={index}>
                                    <label htmlFor={option.value}>
                                        <input
                                            type="checkbox"
                                            name={`${input.label}[${index}]`}
                                            id={option.value}
                                            // checked={input.value.some(item => isAsync ? item.value === option.value : item === option.value)}
                                            checked={isChecked}
                                            // onChange={event => handleChange(event, option.value)}
                                            onChange={event => handleChange(event, option)}
                                            disabled={option.disabled || disabled}
                                        />
                                        <span title={option.label} className="checkbox-text">
                                            <ReactSVG
                                                className="checkbox__unchecked-icon"
                                                src={`/data/svg/checkbox_unchecked.svg`}
                                            />
                                            <ReactSVG
                                                className="checkbox__checked-icon"
                                                src={`/data/svg/checkbox_checked.svg`}
                                            />
                                            <span>{option.label}</span>
                                        </span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </SmartPortal>
        );
    };

    const renderSelectedValue = (item, index, arr) => {
        if (isAsync) {
            return item.label;
        }
        if (options.find(i => item === i.value)) {
            return getLabel(item);
        }
        return "";
    };

    const renderSelectedValues = values => {
        if (!values || (values && values.length === 0)) {
            return showSearch ? "" : placeholder;
        }
        if (showValuesCount) {
            if (values.length === 1) {
                return getLabel(values[0]);
            }
            if (values.length > 1) {
                return `(${values.length}) ${t("select all results")}`;
            }
        }

        return values?.map((v, idx) => (
            <div key={idx} className="combobox-muiltiple-checkbox-selected-value">
                {renderSelectedValue(v)}
            </div>
        ));
    };

    useEffect(() => {
        if (showOptions) {
            const selectOptions = document.querySelector(".optionsWrapper");
            if (selectOptions) {
                if (selectOptions.scrollIntoViewIfNeeded) {
                    selectOptions.scrollIntoViewIfNeeded(false);
                } else {
                    selectOptions.scrollIntoView(false);
                }
            }
            if (searchInputRef && searchInputRef.current) {
                setTimeout(() => {
                    searchInputRef.current.focus();
                }, 200); // 200 ms delay to wait until popup is visible
            }
        }
    }, [showOptions]);

    return (
        <div className={inputClassName} ref={comboboxInputRef}>
            <div className="input-label">
                {label}
                {required && <span className="required-field">*</span>}
            </div>
            <ClickOutsideHolder className={wrapperClassName} onClickOutside={hideOptions}>
                <button
                    tabIndex={0}
                    className={buttonClassName}
                    type="button"
                    onClick={toggleOptions}
                    onKeyDown={handleButtonKeyDown}
                >
                    {isLoading && (
                        <Loader
                            loaderContainerClass="container-loader container-loader-comboBox-multi-checkbox"
                            loaderClass="input-loader"
                        />
                    )}
                    <div
                        className={
                            input && Array.isArray(input.value) && input.value.length > 0
                                ? "input-value"
                                : "placeholder"
                        }
                    >
                        {renderSelectedValues(input.value)}
                    </div>
                    {!disabled && showSearch && (
                        <div
                            className={cx("input-search", {
                                "with-value": input && Array.isArray(input.value) && input.value.length > 0,
                            })}
                        >
                            <ReactSVG className="input-search-icon" src={`/data/svg/search_redesign.svg`} />
                            <input
                                ref={searchInputRef}
                                name={`${input.name}-SEARCH`}
                                value={searchQuery}
                                // placeholder={t("Search")}
                                type="text"
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                    )}
                </button>
                {showOptions && renderCheckboxGroup()}
            </ClickOutsideHolder>
            {showErrorBlock && <ErrorBlock error={meta.error} className="m-r-10" />}
        </div>
    );
};

ComboBoxMultipleCheckbox.propTypes = {
    options: PropTypes.array.isRequired,
    input: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    error: PropTypes.object,
    meta: PropTypes.shape({
        error: PropTypes.string,
        label: PropTypes.string,
        touched: PropTypes.bool,
    }).isRequired,
};

export default withTranslation()(ComboBoxMultipleCheckbox);
